import React, { useState, useEffect, useMemo, useCallback, memo } from "react";
import PropTypes from "prop-types";
import { useGetUser } from "../hooks/useReactQuery";
import "../assets/scss/views/ProfileUser.scss";
import { useAuth } from "../context/AuthContext";
import { useMediaService, useUserService } from "../service";
import ConfirmDeleteModal from "../components/ui/modals/ConfirmDeleteModal";
import MultiDownloadModal from "../components/ui/modals/MultiDownloadModal";
import PreviewModal from "../components/ui/modals/PreviewModal";
import UpdateModal from "../components/ui/modals/UpdateModal";
import DocumentsSectionProfile from "../components/ui/tables/DocumentsSectionProfile";
import { useDocumentTypesByRole } from "../hooks/useFileQuery";
import DocumentFilters, {
  NON_EXPIRABLE_DOCUMENTS,
} from "../components/filters/DocumentFilters";
import { useDownload, useFileTransfer } from "../context/DownloadContext";
import UserProfileInfo from "./UserProfileInfo";
import UserProfileEdit from "./UserProfileEdit";
import { useQueryClient } from "@tanstack/react-query";
import {
  NotificationSeverity,
  useNotification,
} from "../context/NotificationContext";
import LoadingAnimation from "../components/loading/LoadingAnimation";
import { useLayout } from "../context/LayoutContext";

// Iconos para el toggle de vista
import GridViewIcon from "../assets/svg/view/grid_view.svg";
import GridViewLightIcon from "../assets/svg/view/grid_view_light.svg";
import SplitViewIcon from "../assets/svg/view/split_view.svg";
import SplitViewLightIcon from "../assets/svg/view/split_view_light.svg";
import { JOB_POSITIONS } from "../lib/userProfileData";

// Componentes memoizados para optimización
const MemoizedUserProfileInfo = memo(UserProfileInfo);
const MemoizedUserProfileEdit = memo(UserProfileEdit);
const MemoizedDocumentFilters = memo(DocumentFilters);
const MemoizedDocumentsSectionProfile = memo(DocumentsSectionProfile);
const MemoizedPreviewModal = memo(PreviewModal);
const MemoizedMultiDownloadModal = memo(MultiDownloadModal);
const MemoizedConfirmDeleteModal = memo(ConfirmDeleteModal);
const MemoizedUpdateModal = memo(UpdateModal);

// Estado inicial para la información de actualización
const initialUpdateState = {
  personal: {
    firstName: "",
    firstLastName: "",
    secondLastName: "",
    documentNumber: "",
    documentTypeId: "",
    documentExpDate: "",
    documentExpeditionPlace: "",
    nationality_id: "",
    birthDate: "",
    department: "",
    bloodType: "",
    jobPosition: "",
    customJobPosition: "",
  },
  contact: {
    primaryPhoneNumber: "",
    primaryPhoneCountryId: "",
    secondaryPhoneNumber: "",
    secondaryPhoneCountryId: "",
    email: "",
    location: null,
    emergencyContactName: "",
    emergencyContactPhone: "",
  },
  system: {
    roleId: "",
    activeStatus: true,
    statusReason: "",
    limitedNotifications: true,
    ownFleet: true,
    eps: "",
    arl: "",
  },
};

function ProfileUser({ id }) {
  const { theme } = useLayout();
  const { showNotification } = useNotification();
  const { handleUploadDocument } = useFileTransfer();
  const queryClient = useQueryClient();
  const { handleDownloadMultipleDocuments } = useDownload();
  const { data, isLoading, error } = useGetUser(id);
  const { user: userLogin } = useAuth();
  const { documentTypes } = useDocumentTypesByRole(data?.data.role_id);

  // Estados principales
  const [isEditing, setIsEditing] = useState(false);

  // Sistema de vista dual/simple
  const [viewMode, setViewMode] = useState("dual"); // 'simple' o 'dual'

  // Para modo simple
  const [currentView, setCurrentView] = useState("info"); // 'info' o 'documents'
  const [currentDocumentView, setCurrentDocumentView] = useState("required"); // 'required' o 'additional'

  // Para modo dual - paneles independientes
  const [leftPanel, setLeftPanel] = useState({
    view: "info",
  });
  const [rightPanel, setRightPanel] = useState({
    view: "documents",
    documentType: "required", // 'required' o 'additional'
  });

  // Estados para filtrado y búsqueda
  const [searchValue, setSearchValue] = useState("");
  const [filterValue, setFilterValue] = useState("all");

  // Estados para modales (manteniendo los existentes)
  const [openPreview, setOpenPreview] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [openUpdateDialog, setOpenUpdateDialog] = useState(false);
  const [docToUpdate, setDocToUpdate] = useState(null);
  const [newFile, setNewFile] = useState(null);
  const [openMultiDownload, setOpenMultiDownload] = useState(false);
  const [selectedDocs, setSelectedDocs] = useState([]);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [docToDelete, setDocToDelete] = useState(null);

  // Estados para actualización de perfil (manteniendo los existentes)
  const [isUpdating, setIsUpdating] = useState(false);
  const [updateData, setUpdateData] = useState(initialUpdateState);

  // Servicios
  const userService = useUserService();
  const mediaService = useMediaService();

  const user = data?.data;

  // Filtrar documentos por tipo (requeridos vs adicionales)
  const getDocumentsByType = useCallback((documentType) => {
    if (!user?.documents || !documentTypes) return { documents: [], types: [] };

    const isAdditional = documentType === "additional";
    
    // Filtrar tipos de documentos
    const filteredTypes = documentTypes.filter(type => 
      Boolean(type.is_additional) === isAdditional
    );

    // Filtrar documentos del usuario
    const filteredDocuments = user.documents.filter(doc => {
      const docType = filteredTypes.find(type => type.id === doc.document_type?.id);
      return Boolean(docType);
    });

    return { documents: filteredDocuments, types: filteredTypes };
  }, [user?.documents, documentTypes]);

  // Handlers para vista y navegación
  const handleToggleViewMode = useCallback(() => {
    setViewMode((prevMode) => {
      const newMode = prevMode === "simple" ? "dual" : "simple";

      // Sincronizar con paneles al cambiar a dual
      if (newMode === "dual") {
        setLeftPanel({
          view: "info",
        });
        setRightPanel({
          view: "documents",
          documentType: "required",
        });
      }

      return newMode;
    });
  }, []);

  const handleSimpleNavigation = useCallback((view) => {
    setCurrentView(view);
    if (view === "documents" && currentDocumentView === "") {
      setCurrentDocumentView("required");
    }
  }, [currentDocumentView]);

  const handleDocumentTypeNavigation = useCallback((documentType) => {
    if (viewMode === "simple") {
      setCurrentDocumentView(documentType);
    } else {
      setRightPanel(prev => ({
        ...prev,
        documentType
      }));
    }
  }, [viewMode]);

  const handleDualNavigation = useCallback((panel, view) => {
    if (panel === "left") {
      setLeftPanel({
        view,
      });
    } else {
      setRightPanel(prev => ({
        view,
        documentType: view === "documents" ? (prev.documentType || "required") : prev.documentType
      }));
    }
  }, []);

  // Inicialización de datos de usuario (manteniendo código existente)
  useEffect(() => {
    if (user) {
      setUpdateData({
        personal: {
          firstName: user.first_name || "",
          firstLastName: user.first_last_name || "",
          secondLastName: user.second_last_name || "",
          documentNumber: user.document_number || "",
          documentTypeId: user.document_type_id || "",
          documentExpDate: user.document_exp_date
            ? new Date(user.document_exp_date).toISOString().split("T")[0]
            : "",
          documentExpeditionPlace: user.document_expedition_place || "",
          nationality_id: user.nationality_id || "",
          birthDate: user.birth_date
            ? new Date(user.birth_date).toISOString().split("T")[0]
            : "",
          department: user.department || "",
          bloodType: user.blood_type || "",
          jobPosition:
            user.job_position === "custom" ? "custom" : user.job_position || "",
          customJobPosition:
            user.job_position &&
            !JOB_POSITIONS.find((jp) => jp.value === user.job_position)
              ? user.job_position
              : "",
        },
        contact: {
          primaryPhoneNumber: user.primary_phone_number || "",
          primaryPhoneCountryId: user.primary_phone_country_id || "",
          secondaryPhoneNumber: user.secondary_phone_number || "",
          secondaryPhoneCountryId: user.secondary_phone_country_id || "",
          email: user.email || "",
          location: user.location || null,
          emergencyContactName: user.emergency_contact_name || "",
          emergencyContactPhone: user.emergency_contact_phone || "",
        },
        system: {
          roleId: user.role_id || "",
          activeStatus:
            user.active_status !== undefined ? user.active_status : true,
          statusReason: user.status_reason || "",
          limitedNotifications:
            user.limited_notifications !== undefined
              ? user.limited_notifications
              : true,
          ownFleet: user.own_fleet !== undefined ? user.own_fleet : true,
          eps: user.eps || "",
          arl: user.arl || "",
        },
      });
    }
  }, [user]);

  // Preparar documentos para descarga (manteniendo código existente)
  const prepareDocumentsForDownload = useMemo(() => {
    if (!user?.documents) return { personal: [], certificates: [], others: [] };

    const personalDocTypes = [
      "Documento de identidad",
      "Licencia de Conducción",
      "Foto de Perfil",
      "Contrato Laboral",
    ];

    const certificateTypes = [
      "Curso de Sustancias Peligrosas",
      "Curso de Seguridad Vial",
      "Curso de Manejo de Extintores y Contraincendios",
      "Curso Mecánica Básica",
      "Curso Primeros Auxilios",
      "Curso de Manejo Defensivo",
      "Curso Trabajo En Alturas",
      "MAE",
      "Módulos Driving",
    ];

    return {
      personal: user.documents.filter((doc) =>
        personalDocTypes.includes(doc.document_type?.name)
      ),
      certificates: user.documents.filter((doc) =>
        certificateTypes.includes(doc.document_type?.name)
      ),
      others: user.documents.filter(
        (doc) =>
          !personalDocTypes.includes(doc.document_type?.name) &&
          !certificateTypes.includes(doc.document_type?.name)
      ),
    };
  }, [user?.documents]);

  // Lógica de permisos (manteniendo código existente)
  const myRole = userLogin?.role_id;
  const profileRole = user?.role_id;
  const myProfile = userLogin?.id === user?.id;

  const permissionLogic = {
    myRole: userLogin?.role_id,
    profileRole: user?.role_id,
    myProfile: userLogin?.id === user?.id,

    canEditProfile: () => {
      const { myRole, profileRole, myProfile } = permissionLogic;
      return myProfile
        ? myRole === 4 || !(myRole === 5 || myRole === 6)
        : myRole === 4 ||
            (myRole === 1 && (profileRole === 5 || profileRole === 6)) ||
            (myRole === 2 && (profileRole === 5 || profileRole === 6));
    },

    canDownloadDocs: () => {
      const { profileRole, myRole } = permissionLogic;
      return !(profileRole === 4 && myRole !== 4);
    },

    canUpdateOrDeleteDocs: () => permissionLogic.canEditProfile(),
  };

  // Filtrar documentos actualizado para considerar tipo de documento
  const getFilteredDocuments = (documentType = null) => {
    if (!user.documents) return { documents: [], types: [] };

    // Obtener documentos y tipos según el tipo solicitado
    let baseData;
    if (documentType) {
      baseData = getDocumentsByType(documentType);
    } else {
      // Si no se especifica tipo, usar todos
      baseData = { documents: user.documents, types: documentTypes || [] };
    }

    let filtered = [...baseData.documents];
    let relevantTypes = [...baseData.types];

    // Aplicar filtro de búsqueda
    if (searchValue.trim()) {
      const searchTerm = searchValue.toLowerCase();

      filtered = filtered.filter(
        (doc) =>
          doc.original_name_description?.toLowerCase().includes(searchTerm) ||
          doc.file_name?.toLowerCase().includes(searchTerm) ||
          doc.document_type?.name?.toLowerCase().includes(searchTerm)
      );

      relevantTypes = relevantTypes.filter(
        (type) =>
          filtered.some((doc) => doc.document_type?.id === type.id) ||
          type.name?.toLowerCase().includes(searchTerm)
      );
    }

    // Aplicar filtros adicionales (manteniendo lógica existente)
    switch (filterValue) {
      case "expiration":
        const now = new Date();
        const thirtyDaysFromNow = new Date();
        thirtyDaysFromNow.setDate(now.getDate() + 30);

        filtered = filtered.filter((doc) => {
          if (NON_EXPIRABLE_DOCUMENTS.includes(doc.document_type?.name)) {
            return false;
          }

          if (!doc.expiration_date) return false;
          const expirationDate = new Date(doc.expiration_date);
          return expirationDate <= thirtyDaysFromNow;
        });

        relevantTypes = relevantTypes.filter((type) =>
          filtered.some((doc) => doc.document_type?.id === type.id)
        );
        break;

      case "personal":
        const personalDocTypes = [
          "Foto de Perfil",
          "Documento de identidad",
          "Licencia de Conducción",
        ];
        filtered = filtered.filter((doc) =>
          personalDocTypes.includes(doc.document_type?.name)
        );
        relevantTypes = relevantTypes.filter((type) =>
          personalDocTypes.includes(type.name)
        );
        break;

      case "empty":
        const emptyTypes =
          documentTypes?.filter((type) => {
            const docsOfThisType = user.documents.filter(
              (doc) => doc.document_type?.id === type.id
            );

            if (docsOfThisType.length === 0) return true;

            const allDocsAreTemporary = docsOfThisType.every(
              (doc) => doc.id?.startsWith("temp-") || doc.isLocal === true
            );

            return allDocsAreTemporary;
          }) || [];

        filtered = user.documents.filter(
          (doc) =>
            (doc.id?.startsWith("temp-") || doc.isLocal === true) &&
            emptyTypes.some((type) => type.id === doc.document_type?.id)
        );

        relevantTypes = emptyTypes;
        break;

      default:
        break;
    }

    return {
      documents: filtered,
      types: relevantTypes,
    };
  };

  // Handlers de documentos (manteniendo código existente pero adaptado)
  const handleUploadNewDocument = async (newDoc) => {
    if (!newDoc || (!newDoc.file && !newDoc.id?.startsWith("temp-"))) {
      showNotification({
        message: "El documento debe tener un archivo",
        severity: NotificationSeverity.WARNING,
      });
      return;
    }

    try {
      const documentTypeId =
        newDoc.documentTypeId ||
        newDoc.document_type_id ||
        newDoc.document_type?.id;

      if (!documentTypeId) {
        throw {
          validationErrors: {
            documentTypeId: "Tipo de documento requerido",
          },
        };
      }

      const documentToUpload = {
        file: newDoc.file,
        documentTypeId: parseInt(documentTypeId),
        userId: data?.data?.id,
        description:
          newDoc.description ||
          newDoc.original_name_description ||
          newDoc.document_type?.name ||
          "",
        required: newDoc.required || false,
        expeditionDate: newDoc.expedition_date || null,
        expirationDate: newDoc.expiration_date || null,
        fileName: newDoc.file?.name,
        originalName: newDoc.file?.name,
        fileType: newDoc.file?.type,
        fileSize: newDoc.file?.size,
        content: null,
      };
      
      const response = await handleUploadDocument(documentToUpload);

      showNotification({
        message: "Documento agregado exitosamente",
        severity: NotificationSeverity.SUCCESS,
      });

      queryClient.invalidateQueries(["user", id]);
      return response;
    } catch (err) {
      console.error("Error al subir documento:", err);

      if (err.validationErrors) {
        const errorMessages = Object.entries(err.validationErrors)
          .map(([field, message]) => `${field}: ${message}`)
          .join(", ");
        showNotification({
          message: `Validación fallida: ${errorMessages}`,
          severity: NotificationSeverity.ERROR,
        });
      } else {
        showNotification({
          message: err.message || "Error al subir el documento",
          severity: NotificationSeverity.ERROR,
        });
      }

      queryClient.invalidateQueries(["user", id]);
      throw err;
    }
  };

  const documentHandlers = {
    handlePreview: (doc) => {
      setSelectedDoc(doc);
      setOpenPreview(true);
    },

    handleUpdate: (doc) => {
      if (!permissionLogic.canUpdateOrDeleteDocs()) return;
      handleUpdateDoc(doc);
    },

    handleDelete: (doc) => {
      if (!permissionLogic.canUpdateOrDeleteDocs()) return;
      setDocToDelete(doc);
      setOpenDeleteDialog(true);
    },

    handleAddDocument: (newDoc) => {
      if (!permissionLogic.canUpdateOrDeleteDocs()) return;

      if (!newDoc) return;

      const updatedDocuments = [...user.documents, newDoc];

      queryClient.setQueryData(["user", id], {
        ...data,
        data: {
          ...user,
          documents: updatedDocuments,
        },
      });
    },

    handleDocumentChange: (action) => {
      if (!permissionLogic.canUpdateOrDeleteDocs()) return;
      const userId = id;

      switch (action.type) {
        case "ADD_DOCUMENT":
          handleUploadNewDocument(action.payload);
          break;
        case "VALIDATION_ERROR":
          showNotification({
            message:
              action.payload.message || "Error de validación en el documento",
            severity: NotificationSeverity.WARNING,
          });
          break;
        case "UPDATE_DOCUMENT":
          const updatedDocs = user.documents.map((doc) =>
            doc.id === action.payload.docId
              ? { ...doc, [action.payload.field]: action.payload.value }
              : doc
          );

          queryClient.setQueryData(["user", userId], {
            ...data,
            data: {
              ...user,
              documents: updatedDocs,
            },
          });
          break;

        case "DELETE_DOCUMENT":
          if (!docToDelete?.isLocal && !docToDelete?.id?.startsWith("temp-")) {
            confirmDeleteDoc();
          }
          break;

        case "DELETE_LOCAL_DOCUMENT":
          const filteredDocs = user.documents.filter(
            (doc) => doc.id !== action.payload.docId
          );

          queryClient.setQueryData(["user", userId], {
            ...data,
            data: {
              ...user,
              documents: filteredDocs,
            },
          });
          break;

        default:
          console.warn("Acción no reconocida:", action.type);
      }
    },
  };

  // Resto de handlers (manteniendo código existente)
  const handleDownloadAll = async () => {
    if (selectedDocs.length === 0) {
      showNotification({
        message: "No hay documentos seleccionados",
        severity: NotificationSeverity.WARNING,
      });
      return;
    }

    try {
      await handleDownloadMultipleDocuments(selectedDocs, 1);
      showNotification({
        message: "Descarga completada exitosamente",
        severity: NotificationSeverity.SUCCESS,
      });
    } catch (err) {
      showNotification({
        message: "Error al descargar los documentos",
        severity: NotificationSeverity.ERROR,
      });
    }
  };

  const handleUpdateDoc = async (doc) => {
    if (!permissionLogic.canUpdateOrDeleteDocs()) return;

    try {
      const updatePayload = {
        document_type_id: doc.document_type?.id || doc.document_type_id,
        description: doc.original_name_description || "",
        required: doc.required || false,
        expedition_date: doc.expedition_date,
        expiration_date: doc.expiration_date,
        userId: data?.data?.id,
      };

      if (doc.file && doc.file instanceof File) {
        updatePayload.file = doc.file;
        updatePayload.fileName = doc.file.name;
        updatePayload.originalName = doc.file.name;
      }

      await mediaService.updateDocument(doc.id, updatePayload);

      showNotification({
        message: "Documento actualizado exitosamente",
        severity: NotificationSeverity.SUCCESS,
      });

      queryClient.invalidateQueries(["user", id]);
    } catch (err) {
      showNotification({
        message:
          err.response?.data?.message || "Error al actualizar el documento",
        severity: NotificationSeverity.ERROR,
      });
    }
  };

  const confirmDeleteDoc = async () => {
    if (!docToDelete) return;
    try {
      await mediaService.deleteDocument(docToDelete.id);
      showNotification({
        message: "Documento eliminado exitosamente",
        severity: NotificationSeverity.SUCCESS,
      });
      queryClient.invalidateQueries(["user", id]);
    } catch (err) {
      showNotification({
        message: "Error al eliminar el documento",
        severity: NotificationSeverity.ERROR,
      });
      console.error(err);
    } finally {
      setOpenDeleteDialog(false);
      setDocToDelete(null);
    }
  };

  // Handlers para edición de perfil (manteniendo código existente)
  const toggleEditMode = () => {
    setIsEditing(!isEditing);
  };

  const handleUpdateChange = (section, field, value) => {
    setUpdateData((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value,
      },
    }));
  };

  const handleSubmit = async () => {
    setIsUpdating(true);
    try {
      const transformField = (value, isNumeric = false) => {
        if (value === undefined || value === "" || value === null) return null;
        return isNumeric ? parseInt(value, 10) : value;
      };

      const getFinalJobPosition = () => {
        const { jobPosition, customJobPosition } = updateData.personal;

        if (jobPosition === "custom" && customJobPosition?.trim()) {
          return customJobPosition.trim();
        }
        if (jobPosition && jobPosition !== "custom") {
          return jobPosition;
        }

        return null;
      };
      
      const payload = {
        first_name: updateData.personal.firstName,
        first_last_name: updateData.personal.firstLastName,
        second_last_name: transformField(updateData.personal.secondLastName),
        document_number: updateData.personal.documentNumber,
        document_type_id: transformField(
          updateData.personal.documentTypeId,
          true
        ),
        document_exp_date: transformField(updateData.personal.documentExpDate),
        document_expedition_place: transformField(
          updateData.personal.documentExpeditionPlace
        ),
        birth_date: transformField(updateData.personal.birthDate),
        nationality_id: transformField(
          updateData.personal.nationality_id,
          true
        ),
        email: updateData.contact.email,
        primary_phone_number: transformField(
          updateData.contact.primaryPhoneNumber
        ),
        primary_phone_country_id: transformField(
          updateData.contact.primaryPhoneCountryId,
          true
        ),
        secondary_phone_number: transformField(
          updateData.contact.secondaryPhoneNumber
        ),
        secondary_phone_country_id: transformField(
          updateData.contact.secondaryPhoneCountryId,
          true
        ),
        department: transformField(updateData.personal.department),
        blood_type: transformField(updateData.personal.bloodType),
        job_position: transformField(getFinalJobPosition()),
        emergency_contact_name: transformField(
          updateData.contact.emergencyContactName
        ),
        emergency_contact_phone: transformField(
          updateData.contact.emergencyContactPhone
        ),
        role_id: transformField(updateData.system.roleId, true),
        active_status: updateData.system.activeStatus,
        status_reason: updateData.system.activeStatus
          ? null
          : transformField(updateData.system.statusReason),
        limited_notifications: updateData.system.limitedNotifications,
        own_fleet: updateData.system.ownFleet,
        eps: transformField(updateData.system.eps),
        arl: transformField(updateData.system.arl),
      };
      
      const validationErrors = [];
      
      if (!payload.active_status && !payload.status_reason?.trim()) {
        validationErrors.push(
          "La razón de deshabilitación es requerida cuando el usuario está inactivo"
        );
      }

      if (
        updateData.personal.jobPosition === "custom" &&
        !updateData.personal.customJobPosition?.trim()
      ) {
        validationErrors.push(
          'Debe especificar el cargo cuando selecciona "Otro"'
        );
      }

      if (validationErrors.length > 0) {
        showNotification({
          message: `Errores de validación: ${validationErrors.join(", ")}`,
          severity: NotificationSeverity.WARNING,
        });
        return;
      }
      
      const response = await userService.updateUser(id, payload);

      if (response.success) {
        queryClient.invalidateQueries(["user", id]);
        setIsEditing(false);
        showNotification({
          message: "Perfil actualizado exitosamente",
          severity: NotificationSeverity.SUCCESS,
        });
      }
    } catch (error) {
    console.error('Error en handleSubmit:', error);

    let errorMessage = 'Error al actualizar el perfil';
    if (error.response?.data) {
      const serverError = error.response.data;
      
      if (serverError.errors) {
        errorMessage = `Errores de validación: ${serverError.errors.join(', ')}`;
      } 
      else if (serverError.message) {
        errorMessage = serverError.message;
      }
      else if (error.response.status === 409) {
        errorMessage = 'Ya existe un usuario con ese email o número de documento';
      }
      else if (error.response.status === 404) {
        errorMessage = 'Usuario no encontrado';
      }
      else if (error.response.status >= 500) {
        errorMessage = 'Error interno del servidor. Intente nuevamente.';
      }
    } 
    else if (error.request) {
      errorMessage = 'Error de conexión. Verifique su conexión a internet.';
    }
    else if (error.message) {
      errorMessage = error.message;
    }

    showNotification({
      message: errorMessage,
      severity: NotificationSeverity.ERROR,
    });
    } finally {
      setIsUpdating(false);
    }
  };

  const handleOpenMultiDownload = (e) => {
    e.preventDefault();
    setOpenMultiDownload(true);
  };

  // Función para renderizar contenido de panel actualizada
  const renderPanelContent = useCallback(
    (panelConfig, panelType = "full") => {
      const { view, documentType } = panelConfig;

      if (view === "info") {
        return isEditing ? (
          <MemoizedUserProfileEdit
            updateData={updateData}
            onUpdateChange={handleUpdateChange}
            onSubmit={handleSubmit}
            onCancel={toggleEditMode}
            profileImageId={
              user.documents.find((doc) => doc.file_type === "Foto de Perfil")
                ?.id
            }
            isUpdating={isUpdating}
            theme={theme}
          />
        ) : (
          <MemoizedUserProfileInfo
            user={user}
            canEditProfile={permissionLogic.canEditProfile()}
            onEditClick={toggleEditMode}
            profileImageId={
              user.documents.find((doc) => doc.file_type === "Foto de Perfil")
                ?.id
            }
            theme={theme}
          />
        );
      }

      if (view === "documents") {
        const filteredData = getFilteredDocuments(documentType);

        return (
          <>
            <div className={`GM__${theme}-multi-download-container`}>
              <MemoizedDocumentFilters
                documents={user.documents}
                onSearchChange={(e) => setSearchValue(e.target.value)}
                onFilterChange={(e) => setFilterValue(e.target.value)}
                searchValue={searchValue}
                filterValue={filterValue}
                onExpirationFilterClick={() => setFilterValue("expiration")}
                handleOpenMultiDownload={handleOpenMultiDownload}
                theme={theme}
              />
            </div>

            <MemoizedDocumentsSectionProfile
              documents={filteredData.documents}
              dataTypes={filteredData.types}
              onDocumentsChange={documentHandlers.handleDocumentChange}
              isLoading={isLoading}
              canEdit={permissionLogic.canUpdateOrDeleteDocs()}
              onPreview={documentHandlers.handlePreview}
              onUpdate={documentHandlers.handleUpdate}
              onDelete={documentHandlers.handleDelete}
              onAddDocument={documentHandlers.handleAddDocument}
              theme={theme}
            />
          </>
        );
      }

      return null;
    },
    [
      isEditing,
      updateData,
      handleUpdateChange,
      handleSubmit,
      toggleEditMode,
      user,
      permissionLogic,
      theme,
      isUpdating,
      searchValue,
      filterValue,
      getFilteredDocuments,
      documentHandlers,
      isLoading,
      handleOpenMultiDownload,
    ]
  );

  // Estados de carga y error
  if (isLoading) return <LoadingAnimation />;
  if (error) return <div>Error al cargar el usuario: {error.message}</div>;
  if (!data) return <div>No se encontró el usuario</div>;

  // Iconos para el toggle
  const gridViewIcon = theme === "dark" ? GridViewLightIcon : GridViewIcon;
  const splitViewIcon = theme === "dark" ? SplitViewLightIcon : SplitViewIcon;

  return (
    <div className={`UP__${theme}-user-profile ${viewMode}`}>
      {/* Header con toggle de vista */}
      <div className={`UP__${theme}-profile-header-controls`}>
        <div className={`UP__${theme}-view-mode-toggle`}>
          <button
            className={`UP__${theme}-view-toggle-btn`}
            onClick={handleToggleViewMode}
            title={viewMode === "simple" ? "Vista dividida" : "Vista simple"}
          >
            <img
              src={viewMode === "simple" ? splitViewIcon : gridViewIcon}
              alt={viewMode === "simple" ? "Vista dividida" : "Vista simple"}
              className={`UP__${theme}-view-icon`}
            />
          </button>
        </div>
      </div>

      {/* VISTA SIMPLE */}
      {viewMode === "simple" && (
        <>
          {/* Navegación de pestañas CON BOTONES CONECTADOS */}
          <div className={`UP__${theme}-profile-navigation simple-user`}>
            <div className={`UP__${theme}-nav-group`}>
              {/* Botón de perfil independiente */}
              <button
                className={`UP__${theme}-nav-button single ${
                  currentView === "info" ? "active" : ""
                }`}
                onClick={() => handleSimpleNavigation("info")}
              >
                <strong>Perfil</strong>
              </button>

              {/* Grupo de botones para documentos */}
              <div className={`UP__${theme}-button-group`}>
                <button
                  className={`UP__${theme}-nav-button ${
                    currentView === "documents" && currentDocumentView === "required" ? "active" : ""
                  } left`}
                  onClick={() => {
                    handleSimpleNavigation("documents");
                    handleDocumentTypeNavigation("required");
                  }}
                >
                  Documentos <strong>Obligatorios</strong>
                </button>
                <button
                  className={`UP__${theme}-nav-button ${
                    currentView === "documents" && currentDocumentView === "additional" ? "active" : ""
                  } right`}
                  onClick={() => {
                    handleSimpleNavigation("documents");
                    handleDocumentTypeNavigation("additional");
                  }}
                >
                  <strong>Documentos Adicionales</strong>
                </button>
              </div>
            </div>
          </div>

          {/* Contenido simple */}
          <div className={`UP__${theme}-content-wrapper`}>
            {currentView === "info" ? (
              renderPanelContent({ view: "info" })
            ) : (
              renderPanelContent({ 
                view: "documents", 
                documentType: currentDocumentView 
              })
            )}
          </div>
        </>
      )}

      {/* VISTA DUAL */}
      {viewMode === "dual" && (
        <>
          {/* Navegación dual CON BOTONES CONECTADOS */}
          <div className={`UP__${theme}-dual-navigation simple-user`}>
            <div className={`UP__${theme}-dual-section`}>
              <span className={`UP__${theme}-dual-label`}>
                Información del Usuario
              </span>
            </div>

            <div className={`UP__${theme}-dual-section`}>
              <span className={`UP__${theme}-dual-label`}>
                Documentación del Usuario
              </span>
              
              {/* Botones para tipo de documentos en modo dual */}
              <div className={`UP__${theme}-button-group`}>
                <button
                  className={`UP__${theme}-nav-button ${
                    rightPanel.documentType === "required" ? "active" : ""
                  } left`}
                  onClick={() => handleDocumentTypeNavigation("required")}
                >
                  <strong>Obligatorios</strong>
                </button>
                <button
                  className={`UP__${theme}-nav-button ${
                    rightPanel.documentType === "additional" ? "active" : ""
                  } right`}
                  onClick={() => handleDocumentTypeNavigation("additional")}
                >
                  <strong>Adicionales</strong>
                </button>
              </div>
            </div>
          </div>

          {/* Contenido dual */}
          <div className={`UP__${theme}-dual-content-wrapper`}>
            <div className={`UP__${theme}-dual-panel left`}>
              {renderPanelContent(leftPanel, "left")}
            </div>
            <div className={`UP__${theme}-dual-panel right`}>
              {renderPanelContent(rightPanel, "right")}
            </div>
          </div>
        </>
      )}

      {/* Modales (manteniendo código existente) */}
      <MemoizedPreviewModal
        open={openPreview}
        onClose={() => {
          setOpenPreview(false);
          setSelectedDoc(null);
        }}
        doc={selectedDoc}
      />

      <MemoizedUpdateModal
        open={openUpdateDialog}
        onClose={() => {
          setOpenUpdateDialog(false);
          setDocToUpdate(null);
          setNewFile(null);
        }}
        docToUpdate={docToUpdate}
        newFile={newFile}
        setNewFile={setNewFile}
        onConfirmUpdate={async () => {
          if (!newFile || !docToUpdate) return;
          try {
            const formData = new FormData();
            formData.append("file", newFile);

            await mediaService.updateDocument(docToUpdate.id, formData);
            showNotification({
              message: "Documento actualizado exitosamente",
              severity: NotificationSeverity.SUCCESS,
            });
            queryClient.invalidateQueries(["user", id]);
            setOpenUpdateDialog(false);
            setDocToUpdate(null);
            setNewFile(null);
          } catch (err) {
            showNotification({
              message: "Error al actualizar el documento",
              severity: NotificationSeverity.ERROR,
            });
          }
        }}
      />

      <MemoizedMultiDownloadModal
        open={openMultiDownload}
        onClose={() => setOpenMultiDownload(false)}
        personalDocuments={prepareDocumentsForDownload.personal}
        certificates={prepareDocumentsForDownload.certificates}
        otherDocuments={prepareDocumentsForDownload.others}
        selectedDocs={selectedDocs}
        setSelectedDocs={setSelectedDocs}
        onDownloadAll={handleDownloadAll}
        userName={`${user.first_name} ${user.first_last_name} ${user.second_last_name}`}
      />

      <MemoizedConfirmDeleteModal
        open={openDeleteDialog}
        onClose={() => {
          setOpenDeleteDialog(false);
          setDocToDelete(null);
        }}
        onConfirm={confirmDeleteDoc}
        title="Eliminar Documento"
        message={`¿Seguro que deseas eliminar el documento "${docToDelete?.name}"?`}
      />
    </div>
  );
}

ProfileUser.propTypes = {
  id: PropTypes.string.isRequired,
};

export default memo(ProfileUser);